import { z } from "zod/v4";
import { useSyncExternalStore, useCallback } from "react";

const STORAGE_KEY = "palnia_notification_settings";

const notificationSettingsSchema = z.object({
  enabled: z.boolean(),
  timerNotifications: z.boolean(),
  eventNotifications: z.boolean(),
  taskNotifications: z.boolean(),
});

export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: true,
  timerNotifications: true,
  eventNotifications: true,
  taskNotifications: true,
};

let cached: NotificationSettings = loadSettings();
const listeners = new Set<() => void>();

function loadSettings(): NotificationSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = notificationSettingsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function persist(settings: NotificationSettings): void {
  cached = settings;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  for (const listener of listeners) {
    try { listener(); } catch { /* ignore */ }
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): NotificationSettings {
  return cached;
}

export function getNotificationSettings(): NotificationSettings {
  return cached;
}

export function useNotificationSettings() {
  const settings = useSyncExternalStore(subscribe, getSnapshot);

  const update = useCallback((updates: Partial<NotificationSettings>) => {
    persist({ ...cached, ...updates });
  }, []);

  return { settings, update };
}
